import {
  InteractionRequiredAuthError,
  type IPublicClientApplication,
} from '@azure/msal-browser';
import { apiTokenRequest } from '../auth/msalConfig';

const baseUrl = import.meta.env.VITE_API_BASE_URL || '';

async function getToken(msal: IPublicClientApplication): Promise<string> {
  const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0];
  if (!account) throw new Error('No signed-in account');
  try {
    const result = await msal.acquireTokenSilent({ ...apiTokenRequest, account });
    return result.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      await msal.acquireTokenRedirect({ ...apiTokenRequest, account });
    }
    throw err;
  }
}

export async function apiFetch<T>(
  msal: IPublicClientApplication,
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const token = await getToken(msal);
  const res = await fetch(`${baseUrl}${path}`, {
    ...init,
    headers: { ...init.headers, Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `Request failed: ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export async function apiUpload<T>(
  msal: IPublicClientApplication,
  path: string,
  form: FormData,
  method = 'POST',
): Promise<T> {
  const token = await getToken(msal);
  const res = await fetch(`${baseUrl}${path}`, {
    method,
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `Upload failed: ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export async function apiGetBlob(msal: IPublicClientApplication, path: string): Promise<Blob> {
  const token = await getToken(msal);
  const res = await fetch(`${baseUrl}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `Request failed: ${res.status}`);
  }
  return res.blob();
}

// ---- shared enums ----
export type OrgRole = 'HR' | 'ADMIN';
export type JobStatus = 'DRAFT' | 'OPEN' | 'CLOSED';
export type ApplicationStatus =
  | 'PROPOSED'
  | 'UNDER_REVIEW'
  | 'APPROVED'
  | 'REJECTED'
  | 'INTERVIEWING'
  | 'INTERVIEWED'
  | 'HIRED';
export type ReviewDecision = 'APPROVED' | 'DENIED';
export type Recommendation = 'STRONG_HIRE' | 'HIRE' | 'NO_HIRE' | 'STRONG_NO_HIRE';

// ---- me ----
export interface Me {
  id: string;
  email: string;
  displayName: string;
  active: boolean;
  roles: { orgRoles: string[]; isAdmin: boolean; isHr: boolean; isReviewer: boolean; isInterviewer: boolean };
}

// ---- users (admin) ----
export interface AdminUser {
  id: string;
  email: string;
  displayName: string;
  active: boolean;
  createdAt: string;
  roles: OrgRole[];
}

// ---- jobs ----
export interface JobSummary {
  id: string;
  title: string;
  department: string | null;
  location: string | null;
  status: JobStatus;
  reviewThreshold: number;
  createdAt: string;
  createdBy: { id: string; displayName: string };
  candidateCount: number;
  canEdit: boolean;
}
export interface JobDetail extends JobSummary {
  description: string;
}
export interface JobInput {
  title: string;
  description: string;
  department: string | null;
  location: string | null;
  status: JobStatus;
  reviewThreshold: number;
}

// ---- talent pool (people) ----
export interface PoolPerson {
  id: string;
  fullName: string;
  email: string;
  phone: string | null;
  compensation: string | null;
  applicationCount: number;
  createdAt: string;
}
export interface PersonApplication {
  id: string;
  status: ApplicationStatus;
  createdAt: string;
  job: { id: string; title: string; status: JobStatus };
}
export interface PersonDetail {
  id: string;
  fullName: string;
  email: string;
  phone: string | null;
  compensation: string | null;
  createdAt: string;
  applications: PersonApplication[];
}

// ---- applications (process) ----
export interface ApplicationSummary {
  id: string;
  status: ApplicationStatus;
  createdAt: string;
  candidate: { id: string; fullName: string; email: string };
  job: { id: string; title: string; status: JobStatus };
  proposedBy: { displayName: string };
  cv: { filename: string; sizeBytes: number } | null;
}
export interface ReviewTally {
  assignedReviewers: number;
  approvals: number;
  denials: number;
  votesCast: number;
  approvalRatio: number;
  threshold: number;
  meetsThreshold: boolean;
}
export interface ReviewItem {
  id: string;
  decision: ReviewDecision;
  comment: string | null;
  decidedAt: string;
  reviewer: { id: string; displayName: string };
}
export interface FeedbackItem {
  id: string;
  rating: number;
  recommendation: Recommendation;
  strengths: string | null;
  concerns: string | null;
  notes: string | null;
  interviewedAt: string;
  interviewer: { id: string; displayName: string };
}
export interface ApplicationDetail {
  id: string;
  status: ApplicationStatus;
  createdAt: string;
  candidate: { id: string; fullName: string; email: string; phone: string | null; compensation?: string | null };
  job: { id: string; title: string; status: JobStatus; reviewThreshold: number };
  proposedBy: { displayName: string };
  cv: { filename: string; sizeBytes: number } | null;
  interviewer: { assignmentId: string; user: { id: string; displayName: string } } | null;
  canManageAssignments: boolean;
  canManage: boolean;
  rejectionReason?: string | null;
  reviewTally: ReviewTally;
  myReview: { decision: ReviewDecision; comment: string | null } | null;
  reviews?: ReviewItem[];
  feedback?: FeedbackItem[];
  myFeedback: FeedbackItem | null;
  canVote: boolean;
  canEditVote: boolean;
  canAdvance: boolean;
  canReject: boolean;
  canHire: boolean;
  canSubmitFeedback: boolean;
  canEditFeedback: boolean;
}

// ---- assignments ----
export interface AssignableUser {
  id: string;
  displayName: string;
  email: string;
}
export interface Assignee {
  assignmentId: string;
  user: { id: string; displayName: string; email: string };
}
export interface InterviewerAssignee extends Assignee {
  application: { id: string; candidate: { fullName: string } };
}
export interface JobAssignments {
  reviewers: Assignee[];
  interviewers: InterviewerAssignee[];
}

// ---- directory (Graph) ----
export interface DirectoryUser {
  azureOid: string;
  displayName: string;
  email: string;
}

// ---- dashboard / audit ----
export interface DashApplication {
  id: string;
  status: ApplicationStatus;
  candidate: { fullName: string };
  job: { id: string; title: string };
}
export interface Dashboard {
  roles: { isAdmin: boolean; isHr: boolean; isReviewer: boolean; isInterviewer: boolean };
  jobs?: Record<string, number>;
  pipeline?: Record<string, number>;
  awaitingDecision?: DashApplication[];
  underReview?: DashApplication[];
  toReview?: DashApplication[];
  toInterview?: DashApplication[];
}
export interface AuditEntry {
  id: string;
  action: string;
  entityType: string;
  entityId: string;
  metadata: unknown;
  createdAt: string;
  actor: { displayName: string } | null;
}
